import React, { useState, useEffect } from "react";
import { SelectChangeEvent } from "@mui/material";
import fractionToInt from "../../util/fractionToInt";
import { useStore } from "../../store/store";

interface UpdateObj {
  unitSize?: string;
  unitSizeFraction?: string;
  jamb?: string;
  addScreen?: string;
  screenPocket?: string;
  screenColor?: string;
  screenLayout?: string;
  screenMeshColor?: string;
  screenType?: string;
}

const errMsgHigh = "Width can not exceed";
const errMsgLow = "Width must exceed";

function useWidthSelector() {
  const addToQuote = useStore((state) => state.addToQuote);

  const doorType = useStore((state) => state.quote.doorType);
  const bifoldType = doorType?.includes("Window") ? "window" : "door";

  const material = useStore((state) => state.quote.material);

  const width = useStore((state) => state.quote.unitSize);
  const fraction = useStore((state) => state.quote.unitSizeFraction);

  const unitSizeFraction = !fraction ? "0" : fraction;

  const handleError = useStore((state) => state.handleError);

  const errors = useStore((state) => state.errors);

  const screenType = useStore((state) => state.quote.screenType);
  const screen = useStore((state) => state.quote.addScreen);

  const [max, setMax] = useState({ int: 339.25, string: "339 1/4" });

  const isAlum = material === "All Aluminum";

  const min =
    isAlum && doorType?.includes("Window")
      ? { int: 12, string: "12" }
      : { int: 38.375, string: "38 3/8" };

  useEffect(() => {
    if (!bifoldType) return;

    handleError("widthErr", "");

    const overall = fractionToInt(width, fraction);
    let maxVal = { int: 0, string: "" };

    switch (bifoldType) {
      case "door":
        if (isAlum) {
          maxVal = { int: 392.4375, string: "392 7/16" };
        } else {
          maxVal = { int: 339.25, string: "339 1/4" };
        }
        break;
      case "window":
        if (isAlum) {
          maxVal = { int: 297.6875, string: "297 11/16" };
        } else {
          maxVal = { int: 255, string: "255" };
        }
        break;
      default:
        return;
    }

    if (maxVal.int) {
      setMax(maxVal);

      if (overall > maxVal.int) {
        handleError("widthErr", `${errMsgHigh} ${max.string}"`);
      } else if (overall <= maxVal.int && overall >= maxVal.int) {
        handleError("widthErr", "");
        handleError("jambErr", "");
      } else if (overall < min.int) {
        handleError("widthErr", `${errMsgLow} ${min.string}"`);
      }
    }
  }, [bifoldType, isAlum]);

  // useEffect(() => {
  //   if(!isAlum) return
  //   if(doorType?.includes("Window")){
  //     setMax({ int: 48, string: "48" })
  //   }
  // }, [material])

  const handleWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value !== width) {
      let update: UpdateObj = { unitSize: value };

      const overall = fractionToInt(value, fraction);

      if (overall > max.int) {
        handleError("widthErr", `${errMsgHigh} ${max.string}"`);
      } else if (overall <= max.int && overall >= min.int) {
        handleError("widthErr", "");
        update = {
          ...update,
          jamb: "",
          addScreen: "",
          screenPocket: "",
          screenColor: "",
        };
        handleError("jambErr", "");
      } else if (overall < min.int) {
        handleError("widthErr", `${errMsgLow} ${min.string}"`);
      }

      if (screenType === "Centor Flat Screen/Shade") {
        if (
          overall > 348.8125 ||
          (screen === "Add Double Shade" && overall > 316.8125) ||
          (screen === "Add Shade" && overall > 159.8125) ||
          (screen === "Add Combo Screen/Shade" && overall > 161.8125) ||
          (screen === "Add Screen" && overall > 175.8125)
        ) {
          update = {
            ...update,
            addScreen: "",
            screenType: "",
            screenPocket: "",
            screenLayout: "",
            screenMeshColor: "",
            screenColor: "",
            jamb: "",
          };
        }
      }

      addToQuote(update, "unitSize");
    }
  };

  const handleFractionChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    if (value !== fraction) {
      let update: UpdateObj = {};

      if (value === null) {
        update["unitSizeFraction"] = "";
      } else {
        update["unitSizeFraction"] = value;
      }

      const overall = fractionToInt(width, value);

      if (overall > max.int) {
        handleError("widthErr", `${errMsgHigh} ${max.string}"`);
      } else if (overall <= max.int && overall >= min.int) {
        handleError("widthErr", "");
        update = {
          ...update,
          jamb: "",
          addScreen: "",
          screenPocket: "",
          screenColor: "",
        };
        handleError("jambErr", "");
      } else if (overall < min.int) {
        handleError("widthErr", `${errMsgLow} ${min.string}"`);
      }

      if (screenType === "Centor Flat Screen/Shade") {
        if (
          overall > 348.8125 ||
          (screen === "Add Double Shade" && overall > 316.8125) ||
          (screen === "Add Shade" && overall > 159.8125) ||
          (screen === "Add Combo Screen/Shade" && overall > 161.8125) ||
          (screen === "Add Screen" && overall > 175.8125)
        ) {
          update = {
            ...update,
            addScreen: "",
            screenType: "",
            screenPocket: "",
            screenLayout: "",
            screenMeshColor: "",
            screenColor: "",
            jamb: "",
          };
        }
      }

      addToQuote(update);
    }
  };

  return {
    handleFractionChange,
    handleWidthChange,
    widthErr: errors.widthErr,
    unitSizeFraction,
    width,
  };
}

export default useWidthSelector;
